import { useAuth } from '../hooks/useAuth';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HeroSection, HomepageErrorBoundary } from '../components/homepage';
// React imports handled by individual components

// Import components directly (temporarily disable lazy loading for debugging)
import { FeaturesGrid } from '../components/homepage/FeaturesGrid';
import { ScreenshotGallery } from '../components/homepage/ScreenshotGallery';
import { BenefitsSection } from '../components/homepage/BenefitsSection';
import { CTASection } from '../components/homepage/CTASection';
import { Footer, NetworkStatusIndicator, NoScriptFallback } from '../components/ui';
import { HOMEPAGE_SCREENSHOTS } from '../data/screenshots';
import { usePerformanceMonitoring } from '../hooks/usePerformanceMonitoring';
import { useLazyLoad } from '../hooks/useLazyLoad';
// import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { ResourceHints } from '../components/ui/ResourceHints';

export function Homepage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  // Network status is handled by NetworkStatusIndicator component
  // const { isOnline } = useNetworkStatus();
  
  // Enable performance monitoring
  usePerformanceMonitoring(true);
  
  // Lazy load below-the-fold content
  const { elementRef: featuresRef, isInView: featuresInView } = useLazyLoad({ rootMargin: '100px' });
  const { elementRef: screenshotsRef, isInView: screenshotsInView } = useLazyLoad({ rootMargin: '100px' });
  const { elementRef: benefitsRef, isInView: benefitsInView } = useLazyLoad({ rootMargin: '100px' });
  const { elementRef: ctaRef, isInView: ctaInView } = useLazyLoad({ rootMargin: '100px' });

  // Show loading state while checking authentication
  if (loading) {
    return (
      <motion.div 
        className="min-h-screen bg-gray-50 flex items-center justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div 
          className="text-center"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <motion.div 
            className="rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"
            animate={{ rotate: 360 }}
            transition={{ 
              duration: 1, 
              repeat: Infinity, 
              ease: "linear" 
            }}
          />
          <motion.p 
            className="text-gray-600"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Loading...
          </motion.p>
        </motion.div>
      </motion.div>
    );
  }

  // Redirect authenticated users to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSignUpClick = () => {
    try {
      navigate('/login');
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback to direct navigation
      window.location.href = '/login';
    }
  };

  const handleLoginClick = () => {
    try {
      navigate('/login');
    } catch (error) {
      console.error('Navigation error:', error);
      // Fallback to direct navigation
      window.location.href = '/login';
    }
  };

  return (
    <>
      {/* No-script fallback */}
      <NoScriptFallback />
      
      {/* Network status indicator */}
      <NetworkStatusIndicator position="top" />
      
      <div className="min-h-screen bg-white">
        {/* Resource hints for performance optimization */}
        <ResourceHints
          preloadImages={[
            // Preload only critical hero images
            HOMEPAGE_SCREENSHOTS[0]?.src,
          ].filter(Boolean)}
          prefetchRoutes={['/login']} // Only prefetch login, not dashboard
          preloadChunks={['auth']} // Preload auth chunk for faster login
        />
      {/* Header */}
      <motion.header 
        className="bg-white shadow-sm sticky top-0 z-50"
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        role="banner"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 sm:py-6">
            <motion.div 
              className="flex items-center"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
                Bowling League Stats
              </h1>
            </motion.div>
            <motion.nav 
              className="flex items-center space-x-2 sm:space-x-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              role="navigation"
              aria-label="Main navigation"
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 touch-manipulation"
                  aria-label="Sign in to your account"
                >
                  Log In
                </Link>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.05, y: -1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/login"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 touch-manipulation"
                  aria-label="Create a new account and get started"
                >
                  Get Started
                </Link>
              </motion.div>
            </motion.nav>
          </div>
        </div>
      </motion.header>

        {/* Main Content */}
        <main role="main" aria-label="Homepage content" id="main-content">
          {/* Hero Section */}
          <HomepageErrorBoundary>
            <HeroSection 
              onSignUpClick={handleSignUpClick}
              onLoginClick={handleLoginClick}
              isAuthenticated={!!user}
            />
          </HomepageErrorBoundary>

          {/* Features Grid Section */}
          <motion.div 
            ref={featuresRef as any}
            initial={{ opacity: 0 }}
            animate={featuresInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            {featuresInView && (
              <HomepageErrorBoundary>
                <FeaturesGrid />
              </HomepageErrorBoundary>
            )}
          </motion.div>

          {/* Screenshot Gallery Section */}
          <motion.div 
            ref={screenshotsRef as any}
            initial={{ opacity: 0 }}
            animate={screenshotsInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            {screenshotsInView && (
              <HomepageErrorBoundary>
                <ScreenshotGallery screenshots={HOMEPAGE_SCREENSHOTS} />
              </HomepageErrorBoundary>
            )}
          </motion.div>

          {/* Benefits Section */}
          <motion.div 
            ref={benefitsRef as any}
            initial={{ opacity: 0 }}
            animate={benefitsInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            {benefitsInView && (
              <HomepageErrorBoundary>
                <BenefitsSection />
              </HomepageErrorBoundary>
            )}
          </motion.div>

          {/* Final CTA Section */}
          <motion.div 
            ref={ctaRef as any}
            initial={{ opacity: 0 }}
            animate={ctaInView ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.6 }}
          >
            {ctaInView && (
              <HomepageErrorBoundary>
                <CTASection 
                  title="Ready to Elevate Your Bowling Game?"
                  description="Join bowlers who are already using our platform to track their performance, analyze their games, and improve their scores. Get started today and see the difference data-driven insights can make."
                  primaryCTA={{
                    text: "Start Tracking Now",
                    onClick: handleSignUpClick
                  }}
                  secondaryCTA={{
                    text: "Sign In",
                    onClick: handleLoginClick
                  }}
                />
              </HomepageErrorBoundary>
            )}
          </motion.div>
        </main>

        {/* Footer */}
        <HomepageErrorBoundary>
          <Footer />
        </HomepageErrorBoundary>
      </div>
    </>
  );
}

export default Homepage;